// @flow
import * as Immutable from 'immutable';
import ImmutableModel from './ImmutableModel';

import { StructuredAddress } from './StructuredAddress';
import { Geometry } from './Geometry';
import { VisitSlot } from './VisitSlot';
import { OrderDetail } from './OrderDetail';
import { MetricEvent } from './MetricEvent';
import {
    AppFields,
    AssignedUser,
    TimeDistancePair,
    ConstraintViolation
} from './Plan';
import { TimeSlot } from './TimeSlot';
import { AmountTransaction } from './AmountTransaction';
import { Amount } from './Amount';
import { Payment } from './Payment';
import { PaymentInstrument } from './PaymentInstrument';
import { LocationId } from './LocationId';
import { MasterLocation } from './MasterLocation';
import { TourDay } from './TourDay';
import { VisitResource } from './VisitResource';
import { ZoneMapGeometry } from './ZoneMapGeometry';
import { Skills } from './Skills';
import { TaskStatusUpdate } from './StatusUpdate';
import { TaskChecklists, SelectedChecklists } from './TaskChecklists';
import { Team } from './Team';
import { TaskPod } from './TaskPod';
import { GeocodingMetadata } from './GeocodingMetadata';
import { TriggeredGeofence } from './TriggeredGeofence';

import type { GeocodingMetadataModelType } from './GeocodingMetadata';
import type { AmountModelType } from './Amount';
import type { AmountTransactionModelType } from './AmountTransaction';
import type { PaymentModelType } from './Payment';
import type { PaymentInstrumentModelType } from './PaymentInstrument';
import type { OrderDetailModelType } from './OrderDetail';
import type {
    VisitTypeEnum,
    VisitStatusEnum,
    VisitSourceEnum,
    TransactionTypeEnum,
    UserVisitTypeEnum,
    AppFieldsModelType,
    AssignedUserModelType,
    TimeDistancePairModelType,
    ConstraintViolationModelType
} from './Plan';
import type {
    TaskStatusEnumType,
    TaskSourceEnumType
} from 'common/enums/TaskStatus';
import type { BreakTypeEnum } from './BreakShift';
import type { VisitSlotModelType } from './VisitSlot';
import type { StructuredAddressModelType } from './StructuredAddress';
import type { GeometryModelType } from './Geometry';
import type { LocationIdModelType } from './LocationId';
import type {
    TaskChecklistsModelType,
    SelectedChecklistsModelType
} from './TaskChecklists';
import type { MetricEventModelType } from './MetricEvent';
import type { TimeSlotModelType } from './TimeSlot';
import type { MasterLocationModelType } from './MasterLocation';
import type { TourDayModelType } from './TourDay';
import type { VisitResourceModelType } from './VisitResource';
import type { ZoneMapGeometryModelType } from './ZoneMapGeometry';
import type { SkillsModelType } from './Skills';
import type { TeamModelType } from './Team';
import type { TaskStatusUpdateModelType } from './StatusUpdate';
import type { TaskPodModelType } from './TaskPod';
import type { TriggeredGeofenceModelType } from './TriggeredGeofence';

export type SlaStatusEnum = 'ON_TIME' | 'LATE' | 'NOT_APPLICABLE';
export type TaskVisitModelType = {
    id: string,
    guid?: ?string,
    appFields: AppFieldsModelType,
    assignedUser: ?AssignedUserModelType,
    batchId: ?string,
    breakType: ?BreakTypeEnum,
    cancelledReason: ?string,
    cancelledSource: ?string,
    checklists: TaskChecklistsModelType,
    clientId: string,
    customerInput: ?SelectedChecklistsModelType,
    customFields: { [key: string]: string },
    currentEta: ?string,
    currentEtd: ?string,
    customerNotes: Array<string>,
    events: ?MetricEventModelType,
    firstAssignedUser: ?AssignedUserModelType,
    initialEta: ?string,
    initialEtd: ?string,
    initialSlot: TimeSlotModelType,
    etaEstimatedOn: ?string,
    isRescheduled: boolean,
    justModified: boolean,
    locationDetails: ?MasterLocationModelType,
    netAmount: number,
    paymentCollected: number,
    pod: TaskPodModelType,
    poc: TaskPodModelType,
    proofOfStarted: TaskPodModelType,
    proofOfArrived: TaskPodModelType,
    proofOfTransacting: TaskPodModelType,
    possibleDays: ?Array<TourDayModelType>,
    predictedTravel: ?TimeDistancePairModelType,
    resources: Array<VisitResourceModelType>,
    routeId: ?string,
    routes: Array<ZoneMapGeometryModelType>,
    skills: SkillsModelType,
    slaBreached: boolean,
    slaStatus: SlaStatusEnum,
    sourceId: ?string,
    sourceOrderId: ?string,
    statusUpdates: TaskStatusUpdateModelType,
    taskPriority: ?number,
    taskSource: TaskSourceEnumType,
    taskStatus: TaskStatusEnumType,
    team: TeamModelType,
    tourId: ?string,
    trackLink: ?string,
    transactionDuration: ?number,
    transactionProgress: number,
    transactionType: TransactionTypeEnum,
    travelProgress: number,
    userVisitType: UserVisitTypeEnum,
    violations: Array<ConstraintViolationModelType>,
    visitDate: ?string,
    visitDateIndex: ?number,
    visitName: ?string,
    visitSource: VisitSourceEnum,
    visitType: VisitTypeEnum,
    visitStatus: VisitStatusEnum,
    chosenLocation: ChosenLocationModelType,
    volume: VolumeModelType,
    volumeFilled: number,
    orderDetail: OrderDetailModelType,
    amountTransaction: AmountTransactionModelType,
    payments: PaymentsModelType,
    isVisitTypeSelected?: boolean,
    isHomebaseVisit?: boolean,
    locationId: ?LocationIdModelType,
    triggeredGeofences: Array<TriggeredGeofenceModelType>
};

export type ContactModelType = {
    name: string,
    number: string,
    alternateNumber: ?string,
    countryCode?: ?string,
    email?: string
};

export type VolumeModelType = {
    value: string,
    exchangeType: string
};

export type TaskSlotModelType = {
    id: string,
    slot: VisitSlotModelType
};

export type ChosenLocationModelType = {
    slot: VisitSlotModelType,
    contact: ?ContactModelType,
    address: StructuredAddressModelType,
    geometry: ?GeometryModelType,
    geocodingMetadata: GeocodingMetadataModelType,
    nonAvailableSlots: Array<TaskSlotModelType>,
    id: string
};

export type PaymentsModelType = {
    totalAmount: AmountModelType,
    pendingAmount: AmountModelType,
    paymentInstruments: Array<PaymentInstrumentModelType>,
    payments: Array<PaymentModelType>,
    fullAmountRequired: boolean
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TaskVisit extends ImmutableModel {
    static fromJS(json: TaskVisitModelType): TaskVisit {
        const state: Object = Object.assign({}, json);
        state.appFields = AppFields.fromJS(state.appFields);
        state.assignedUser = state.assignedUser
            ? AssignedUser.fromJS(state.assignedUser)
            : state.assignedUser;
        state.checklists = TaskChecklists.fromJS(state.checklists);
        state.customerInput = state.customerInput
            ? SelectedChecklists.fromJS(state.customerInput)
            : state.customerInput;
        state.customFields = Immutable.Map(state.customFields);
        state.customerNotes = Immutable.List(state.customerNotes);
        state.events = state.events
            ? MetricEvent.fromJS(state.events)
            : state.events;
        state.firstAssignedUser = state.firstAssignedUser
            ? AssignedUser.fromJS(state.firstAssignedUser)
            : state.firstAssignedUser;
        state.initialSlot = TimeSlot.fromJS(state.initialSlot);
        state.locationDetails = state.locationDetails
            ? MasterLocation.fromJS(state.locationDetails)
            : state.locationDetails;
        state.pod = TaskPod.fromJS(state.pod);
        state.poc = TaskPod.fromJS(state.poc);
        state.proofOfStarted = TaskPod.fromJS(state.proofOfStarted);
        state.proofOfArrived = TaskPod.fromJS(state.proofOfArrived);
        state.proofOfTransacting = TaskPod.fromJS(state.proofOfTransacting);
        state.possibleDays = state.possibleDays
            ? Immutable.List(state.possibleDays).map(item =>
                  TourDay.fromJS(item)
              )
            : state.possibleDays;
        state.predictedTravel = state.predictedTravel
            ? TimeDistancePair.fromJS(state.predictedTravel)
            : state.predictedTravel;
        state.resources = Immutable.List(state.resources).map(item =>
            VisitResource.fromJS(item)
        );
        state.routes = Immutable.List(state.routes).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.skills = Skills.fromJS(state.skills);
        state.statusUpdates = TaskStatusUpdate.fromJS(state.statusUpdates);
        state.team = Team.fromJS(state.team);
        state.violations = Immutable.List(state.violations).map(item =>
            ConstraintViolation.fromJS(item)
        );
        state.chosenLocation = ChosenLocation.fromJS(state.chosenLocation);
        state.volume = Volume.fromJS(state.volume);
        state.orderDetail = OrderDetail.fromJS(state.orderDetail);
        state.amountTransaction = AmountTransaction.fromJS(
            state.amountTransaction
        );
        state.payments = Payments.fromJS(state.payments);
        state.locationId = state.locationId
            ? LocationId.fromJS(state.locationId)
            : state.locationId;
        state.triggeredGeofences = Immutable.List(
            state.triggeredGeofences
        ).map(item => TriggeredGeofence.fromJS(item));
        return new this(Immutable.Map(state));
    }

    toJS(): TaskVisitModelType {
        const js: TaskVisitModelType = {
            id: this.id,
            appFields: this.appFields.toJS(),
            assignedUser: this.assignedUser
                ? this.assignedUser.toJS()
                : this.assignedUser,
            batchId: this.batchId,
            breakType: this.breakType,
            cancelledReason: this.cancelledReason,
            cancelledSource: this.cancelledSource,
            checklists: this.checklists.toJS(),
            clientId: this.clientId,
            customerInput: this.customerInput
                ? this.customerInput.toJS()
                : this.customerInput,
            customFields: this.customFields.toObject(),
            currentEta: this.currentEta,
            currentEtd: this.currentEtd,
            customerNotes: this.customerNotes.toArray(),
            events: this.events ? this.events.toJS() : this.events,
            firstAssignedUser: this.firstAssignedUser
                ? this.firstAssignedUser.toJS()
                : this.firstAssignedUser,
            initialEta: this.initialEta,
            initialEtd: this.initialEtd,
            initialSlot: this.initialSlot.toJS(),
            etaEstimatedOn: this.etaEstimatedOn,
            isRescheduled: this.isRescheduled,
            justModified: this.justModified,
            locationDetails: this.locationDetails
                ? this.locationDetails.toJS()
                : this.locationDetails,
            netAmount: this.netAmount,
            paymentCollected: this.paymentCollected,
            pod: this.pod.toJS(),
            poc: this.poc.toJS(),
            proofOfStarted: this.proofOfStarted.toJS(),
            proofOfArrived: this.proofOfArrived.toJS(),
            proofOfTransacting: this.proofOfTransacting.toJS(),
            possibleDays: this.possibleDays
                ? this.possibleDays.toArray().map(item => item.toJS())
                : this.possibleDays,
            predictedTravel: this.predictedTravel
                ? this.predictedTravel.toJS()
                : this.predictedTravel,
            resources: this.resources.toArray().map(item => item.toJS()),
            routeId: this.routeId,
            routes: this.routes.toArray().map(item => item.toJS()),
            skills: this.skills.toJS(),
            slaBreached: this.slaBreached,
            slaStatus: this.slaStatus,
            sourceId: this.sourceId,
            sourceOrderId: this.sourceOrderId,
            statusUpdates: this.statusUpdates.toJS(),
            taskPriority: this.taskPriority,
            taskSource: this.taskSource,
            taskStatus: this.taskStatus,
            team: this.team.toJS(),
            tourId: this.tourId,
            trackLink: this.trackLink,
            transactionDuration: this.transactionDuration,
            transactionProgress: this.transactionProgress,
            transactionType: this.transactionType,
            travelProgress: this.travelProgress,
            userVisitType: this.userVisitType,
            violations: this.violations.toArray().map(item => item.toJS()),
            visitDate: this.visitDate,
            visitDateIndex: this.visitDateIndex,
            visitName: this.visitName,
            visitSource: this.visitSource,
            visitType: this.visitType,
            visitStatus: this.visitStatus,
            chosenLocation: this.chosenLocation.toJS(),
            volume: this.volume.toJS(),
            volumeFilled: this.volumeFilled,
            orderDetail: this.orderDetail.toJS(),
            amountTransaction: this.amountTransaction.toJS(),
            payments: this.payments.toJS(),
            locationId: this.locationId
                ? this.locationId.toJS()
                : this.locationId,
            triggeredGeofences: this.triggeredGeofences
                .toArray()
                .map(item => item.toJS())
        };

        if (this.guid != null) {
            js.guid = this.guid;
        }

        if (this.isVisitTypeSelected != null) {
            js.isVisitTypeSelected = this.isVisitTypeSelected;
        }

        if (this.isHomebaseVisit != null) {
            js.isHomebaseVisit = this.isHomebaseVisit;
        }

        return js;
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get guid(): ?string {
        return this._state.get('guid');
    }

    setGuid(guid: ?string): this {
        return this.clone(this._state.set('guid', guid));
    }

    get appFields(): AppFields {
        return this._state.get('appFields');
    }

    setAppFields(appFields: AppFields): this {
        return this.clone(this._state.set('appFields', appFields));
    }

    get assignedUser(): ?AssignedUser {
        return this._state.get('assignedUser');
    }

    setAssignedUser(assignedUser: ?AssignedUser): this {
        return this.clone(this._state.set('assignedUser', assignedUser));
    }

    get batchId(): ?string {
        return this._state.get('batchId');
    }

    setBatchId(batchId: ?string): this {
        return this.clone(this._state.set('batchId', batchId));
    }

    get breakType(): ?BreakTypeEnum {
        return this._state.get('breakType');
    }

    setBreakType(breakType: ?BreakTypeEnum): this {
        return this.clone(this._state.set('breakType', breakType));
    }

    get cancelledReason(): ?string {
        return this._state.get('cancelledReason');
    }

    setCancelledReason(cancelledReason: ?string): this {
        return this.clone(this._state.set('cancelledReason', cancelledReason));
    }

    get cancelledSource(): ?string {
        return this._state.get('cancelledSource');
    }

    setCancelledSource(cancelledSource: ?string): this {
        return this.clone(this._state.set('cancelledSource', cancelledSource));
    }

    get checklists(): TaskChecklists {
        return this._state.get('checklists');
    }

    setChecklists(checklists: TaskChecklists): this {
        return this.clone(this._state.set('checklists', checklists));
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get customerInput(): ?SelectedChecklists {
        return this._state.get('customerInput');
    }

    setCustomerInput(customerInput: ?SelectedChecklists): this {
        return this.clone(this._state.set('customerInput', customerInput));
    }

    get customFields(): Immutable.Map<string, string> {
        return this._state.get('customFields');
    }

    setCustomFields(customFields: Immutable.Map<string, string>): this {
        return this.clone(this._state.set('customFields', customFields));
    }

    get currentEta(): ?string {
        return this._state.get('currentEta');
    }

    setCurrentEta(currentEta: ?string): this {
        return this.clone(this._state.set('currentEta', currentEta));
    }

    get currentEtd(): ?string {
        return this._state.get('currentEtd');
    }

    setCurrentEtd(currentEtd: ?string): this {
        return this.clone(this._state.set('currentEtd', currentEtd));
    }

    get customerNotes(): Immutable.List<string> {
        return this._state.get('customerNotes');
    }

    setCustomerNotes(customerNotes: Immutable.List<string>): this {
        return this.clone(this._state.set('customerNotes', customerNotes));
    }

    get events(): ?MetricEvent {
        return this._state.get('events');
    }

    setEvents(events: ?MetricEvent): this {
        return this.clone(this._state.set('events', events));
    }

    get firstAssignedUser(): ?AssignedUser {
        return this._state.get('firstAssignedUser');
    }

    setFirstAssignedUser(firstAssignedUser: ?AssignedUser): this {
        return this.clone(
            this._state.set('firstAssignedUser', firstAssignedUser)
        );
    }

    get initialEta(): ?string {
        return this._state.get('initialEta');
    }

    setInitialEta(initialEta: ?string): this {
        return this.clone(this._state.set('initialEta', initialEta));
    }

    get initialEtd(): ?string {
        return this._state.get('initialEtd');
    }

    setInitialEtd(initialEtd: ?string): this {
        return this.clone(this._state.set('initialEtd', initialEtd));
    }

    get initialSlot(): TimeSlot {
        return this._state.get('initialSlot');
    }

    setInitialSlot(initialSlot: TimeSlot): this {
        return this.clone(this._state.set('initialSlot', initialSlot));
    }

    get etaEstimatedOn(): ?string {
        return this._state.get('etaEstimatedOn');
    }

    setEtaEstimatedOn(etaEstimatedOn: ?string): this {
        return this.clone(this._state.set('etaEstimatedOn', etaEstimatedOn));
    }

    get isRescheduled(): boolean {
        return this._state.get('isRescheduled');
    }

    setIsRescheduled(isRescheduled: boolean): this {
        return this.clone(this._state.set('isRescheduled', isRescheduled));
    }

    get justModified(): boolean {
        return this._state.get('justModified');
    }

    setJustModified(justModified: boolean): this {
        return this.clone(this._state.set('justModified', justModified));
    }

    get locationDetails(): ?MasterLocation {
        return this._state.get('locationDetails');
    }

    setLocationDetails(locationDetails: ?MasterLocation): this {
        return this.clone(this._state.set('locationDetails', locationDetails));
    }

    get netAmount(): number {
        return this._state.get('netAmount');
    }

    setNetAmount(netAmount: number): this {
        return this.clone(this._state.set('netAmount', netAmount));
    }

    get paymentCollected(): number {
        return this._state.get('paymentCollected');
    }

    setPaymentCollected(paymentCollected: number): this {
        return this.clone(
            this._state.set('paymentCollected', paymentCollected)
        );
    }

    get pod(): TaskPod {
        return this._state.get('pod');
    }

    setPod(pod: TaskPod): this {
        return this.clone(this._state.set('pod', pod));
    }

    get poc(): TaskPod {
        return this._state.get('poc');
    }

    setPoc(poc: TaskPod): this {
        return this.clone(this._state.set('poc', poc));
    }

    get proofOfStarted(): TaskPod {
        return this._state.get('proofOfStarted');
    }

    setProofOfStarted(proofOfStarted: TaskPod): this {
        return this.clone(this._state.set('proofOfStarted', proofOfStarted));
    }

    get proofOfArrived(): TaskPod {
        return this._state.get('proofOfArrived');
    }

    setProofOfArrived(proofOfArrived: TaskPod): this {
        return this.clone(this._state.set('proofOfArrived', proofOfArrived));
    }

    get proofOfTransacting(): TaskPod {
        return this._state.get('proofOfTransacting');
    }

    setProofOfTransacting(proofOfTransacting: TaskPod): this {
        return this.clone(
            this._state.set('proofOfTransacting', proofOfTransacting)
        );
    }

    get possibleDays(): ?Immutable.List<TourDay> {
        return this._state.get('possibleDays');
    }

    setPossibleDays(possibleDays: ?Immutable.List<TourDay>): this {
        return this.clone(this._state.set('possibleDays', possibleDays));
    }

    get predictedTravel(): ?TimeDistancePair {
        return this._state.get('predictedTravel');
    }

    setPredictedTravel(predictedTravel: ?TimeDistancePair): this {
        return this.clone(this._state.set('predictedTravel', predictedTravel));
    }

    get resources(): Immutable.List<VisitResource> {
        return this._state.get('resources');
    }

    setResources(resources: Immutable.List<VisitResource>): this {
        return this.clone(this._state.set('resources', resources));
    }

    get routeId(): ?string {
        return this._state.get('routeId');
    }

    setRouteId(routeId: ?string): this {
        return this.clone(this._state.set('routeId', routeId));
    }

    get routes(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('routes');
    }

    setRoutes(routes: Immutable.List<ZoneMapGeometry>): this {
        return this.clone(this._state.set('routes', routes));
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    setSkills(skills: Skills): this {
        return this.clone(this._state.set('skills', skills));
    }

    get slaBreached(): boolean {
        return this._state.get('slaBreached');
    }

    setSlaBreached(slaBreached: boolean): this {
        return this.clone(this._state.set('slaBreached', slaBreached));
    }

    get slaStatus(): SlaStatusEnum {
        return this._state.get('slaStatus');
    }

    setSlaStatus(slaStatus: SlaStatusEnum): this {
        return this.clone(this._state.set('slaStatus', slaStatus));
    }

    get sourceId(): ?string {
        return this._state.get('sourceId');
    }

    setSourceId(sourceId: ?string): this {
        return this.clone(this._state.set('sourceId', sourceId));
    }

    get sourceOrderId(): ?string {
        return this._state.get('sourceOrderId');
    }

    setSourceOrderId(sourceOrderId: ?string): this {
        return this.clone(this._state.set('sourceOrderId', sourceOrderId));
    }

    get statusUpdates(): TaskStatusUpdate {
        return this._state.get('statusUpdates');
    }

    setStatusUpdates(statusUpdates: TaskStatusUpdate): this {
        return this.clone(this._state.set('statusUpdates', statusUpdates));
    }

    get taskPriority(): ?number {
        return this._state.get('taskPriority');
    }

    setTaskPriority(taskPriority: ?number): this {
        return this.clone(this._state.set('taskPriority', taskPriority));
    }

    get taskSource(): TaskSourceEnumType {
        return this._state.get('taskSource');
    }

    setTaskSource(taskSource: TaskSourceEnumType): this {
        return this.clone(this._state.set('taskSource', taskSource));
    }

    get taskStatus(): TaskStatusEnumType {
        return this._state.get('taskStatus');
    }

    setTaskStatus(taskStatus: TaskStatusEnumType): this {
        return this.clone(this._state.set('taskStatus', taskStatus));
    }

    get team(): Team {
        return this._state.get('team');
    }

    setTeam(team: Team): this {
        return this.clone(this._state.set('team', team));
    }

    get tourId(): ?string {
        return this._state.get('tourId');
    }

    setTourId(tourId: ?string): this {
        return this.clone(this._state.set('tourId', tourId));
    }

    get trackLink(): ?string {
        return this._state.get('trackLink');
    }

    setTrackLink(trackLink: ?string): this {
        return this.clone(this._state.set('trackLink', trackLink));
    }

    get transactionDuration(): ?number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: ?number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }

    get transactionProgress(): number {
        return this._state.get('transactionProgress');
    }

    setTransactionProgress(transactionProgress: number): this {
        return this.clone(
            this._state.set('transactionProgress', transactionProgress)
        );
    }

    get transactionType(): TransactionTypeEnum {
        return this._state.get('transactionType');
    }

    setTransactionType(transactionType: TransactionTypeEnum): this {
        return this.clone(this._state.set('transactionType', transactionType));
    }

    get travelProgress(): number {
        return this._state.get('travelProgress');
    }

    setTravelProgress(travelProgress: number): this {
        return this.clone(this._state.set('travelProgress', travelProgress));
    }

    get userVisitType(): UserVisitTypeEnum {
        return this._state.get('userVisitType');
    }

    setUserVisitType(userVisitType: UserVisitTypeEnum): this {
        return this.clone(this._state.set('userVisitType', userVisitType));
    }

    get violations(): Immutable.List<ConstraintViolation> {
        return this._state.get('violations');
    }

    setViolations(violations: Immutable.List<ConstraintViolation>): this {
        return this.clone(this._state.set('violations', violations));
    }

    get visitDate(): ?string {
        return this._state.get('visitDate');
    }

    setVisitDate(visitDate: ?string): this {
        return this.clone(this._state.set('visitDate', visitDate));
    }

    get visitDateIndex(): ?number {
        return this._state.get('visitDateIndex');
    }

    setVisitDateIndex(visitDateIndex: ?number): this {
        return this.clone(this._state.set('visitDateIndex', visitDateIndex));
    }

    get visitName(): ?string {
        return this._state.get('visitName');
    }

    setVisitName(visitName: ?string): this {
        return this.clone(this._state.set('visitName', visitName));
    }

    get visitSource(): VisitSourceEnum {
        return this._state.get('visitSource');
    }

    setVisitSource(visitSource: VisitSourceEnum): this {
        return this.clone(this._state.set('visitSource', visitSource));
    }

    get visitType(): VisitTypeEnum {
        return this._state.get('visitType');
    }

    setVisitType(visitType: VisitTypeEnum): this {
        return this.clone(this._state.set('visitType', visitType));
    }

    get visitStatus(): VisitStatusEnum {
        return this._state.get('visitStatus');
    }

    setVisitStatus(visitStatus: VisitStatusEnum): this {
        return this.clone(this._state.set('visitStatus', visitStatus));
    }

    get chosenLocation(): ChosenLocation {
        return this._state.get('chosenLocation');
    }

    setChosenLocation(chosenLocation: ChosenLocation): this {
        return this.clone(this._state.set('chosenLocation', chosenLocation));
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    setVolume(volume: Volume): this {
        return this.clone(this._state.set('volume', volume));
    }

    get volumeFilled(): number {
        return this._state.get('volumeFilled');
    }

    setVolumeFilled(volumeFilled: number): this {
        return this.clone(this._state.set('volumeFilled', volumeFilled));
    }

    get orderDetail(): OrderDetail {
        return this._state.get('orderDetail');
    }

    setOrderDetail(orderDetail: OrderDetail): this {
        return this.clone(this._state.set('orderDetail', orderDetail));
    }

    get amountTransaction(): AmountTransaction {
        return this._state.get('amountTransaction');
    }

    setAmountTransaction(amountTransaction: AmountTransaction): this {
        return this.clone(
            this._state.set('amountTransaction', amountTransaction)
        );
    }

    get payments(): Payments {
        return this._state.get('payments');
    }

    setPayments(payments: Payments): this {
        return this.clone(this._state.set('payments', payments));
    }

    get isVisitTypeSelected(): ?boolean {
        return this._state.get('isVisitTypeSelected');
    }

    setIsVisitTypeSelected(isVisitTypeSelected: ?boolean): this {
        return this.clone(
            this._state.set('isVisitTypeSelected', isVisitTypeSelected)
        );
    }

    get isHomebaseVisit(): ?boolean {
        return this._state.get('isHomebaseVisit');
    }

    setIsHomebaseVisit(isHomebaseVisit: ?boolean): this {
        return this.clone(this._state.set('isHomebaseVisit', isHomebaseVisit));
    }

    get locationId(): ?LocationId {
        return this._state.get('locationId');
    }

    setLocationId(locationId: ?LocationId): this {
        return this.clone(this._state.set('locationId', locationId));
    }

    get triggeredGeofences(): Immutable.List<TriggeredGeofence> {
        return this._state.get('triggeredGeofences');
    }

    setTriggeredGeofences(
        triggeredGeofences: Immutable.List<TriggeredGeofence>
    ): this {
        return this.clone(
            this._state.set('triggeredGeofences', triggeredGeofences)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class ChosenLocation extends ImmutableModel {
    static fromJS(json: ChosenLocationModelType): ChosenLocation {
        const state: Object = Object.assign({}, json);
        state.slot = VisitSlot.fromJS(state.slot);
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.address = StructuredAddress.fromJS(state.address);
        state.geometry = state.geometry
            ? Geometry.fromJS(state.geometry)
            : state.geometry;
        state.geocodingMetadata = GeocodingMetadata.fromJS(
            state.geocodingMetadata
        );
        state.nonAvailableSlots = Immutable.List(
            state.nonAvailableSlots
        ).map(item => TaskSlot.fromJS(item));
        return new this(Immutable.Map(state));
    }

    toJS(): ChosenLocationModelType {
        return {
            slot: this.slot.toJS(),
            contact: this.contact ? this.contact.toJS() : this.contact,
            address: this.address.toJS(),
            geometry: this.geometry ? this.geometry.toJS() : this.geometry,
            geocodingMetadata: this.geocodingMetadata.toJS(),
            nonAvailableSlots: this.nonAvailableSlots
                .toArray()
                .map(item => item.toJS()),
            id: this.id
        };
    }

    get slot(): VisitSlot {
        return this._state.get('slot');
    }

    setSlot(slot: VisitSlot): this {
        return this.clone(this._state.set('slot', slot));
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    setContact(contact: ?Contact): this {
        return this.clone(this._state.set('contact', contact));
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    setAddress(address: StructuredAddress): this {
        return this.clone(this._state.set('address', address));
    }

    get geometry(): ?Geometry {
        return this._state.get('geometry');
    }

    setGeometry(geometry: ?Geometry): this {
        return this.clone(this._state.set('geometry', geometry));
    }

    get geocodingMetadata(): GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    setGeocodingMetadata(geocodingMetadata: GeocodingMetadata): this {
        return this.clone(
            this._state.set('geocodingMetadata', geocodingMetadata)
        );
    }

    get nonAvailableSlots(): Immutable.List<TaskSlot> {
        return this._state.get('nonAvailableSlots');
    }

    setNonAvailableSlots(nonAvailableSlots: Immutable.List<TaskSlot>): this {
        return this.clone(
            this._state.set('nonAvailableSlots', nonAvailableSlots)
        );
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Contact extends ImmutableModel {
    static fromJS(json: ContactModelType): Contact {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): ContactModelType {
        const js: ContactModelType = {
            name: this.name,
            number: this.number,
            alternateNumber: this.alternateNumber
        };

        if (this.countryCode != null) {
            js.countryCode = this.countryCode;
        }

        if (this.email != null) {
            js.email = this.email;
        }

        return js;
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get number(): string {
        return this._state.get('number');
    }

    setNumber(number: string): this {
        return this.clone(this._state.set('number', number));
    }

    get alternateNumber(): ?string {
        return this._state.get('alternateNumber');
    }

    setAlternateNumber(alternateNumber: ?string): this {
        return this.clone(this._state.set('alternateNumber', alternateNumber));
    }

    get countryCode(): ?string {
        return this._state.get('countryCode');
    }

    setCountryCode(countryCode: ?string): this {
        return this.clone(this._state.set('countryCode', countryCode));
    }

    get email(): ?string {
        return this._state.get('email');
    }

    setEmail(email: ?string): this {
        return this.clone(this._state.set('email', email));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Volume extends ImmutableModel {
    static fromJS(json: VolumeModelType): Volume {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VolumeModelType {
        return {
            value: this.value,
            exchangeType: this.exchangeType
        };
    }

    get value(): string {
        return this._state.get('value');
    }

    setValue(value: string): this {
        return this.clone(this._state.set('value', value));
    }

    get exchangeType(): string {
        return this._state.get('exchangeType');
    }

    setExchangeType(exchangeType: string): this {
        return this.clone(this._state.set('exchangeType', exchangeType));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Payments extends ImmutableModel {
    static fromJS(json: PaymentsModelType): Payments {
        const state: Object = Object.assign({}, json);
        state.totalAmount = Amount.fromJS(state.totalAmount);
        state.pendingAmount = Amount.fromJS(state.pendingAmount);
        state.paymentInstruments = Immutable.List(
            state.paymentInstruments
        ).map(item => PaymentInstrument.fromJS(item));
        state.payments = Immutable.List(state.payments).map(item =>
            Payment.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): PaymentsModelType {
        return {
            totalAmount: this.totalAmount.toJS(),
            pendingAmount: this.pendingAmount.toJS(),
            paymentInstruments: this.paymentInstruments
                .toArray()
                .map(item => item.toJS()),
            payments: this.payments.toArray().map(item => item.toJS()),
            fullAmountRequired: this.fullAmountRequired
        };
    }

    get totalAmount(): Amount {
        return this._state.get('totalAmount');
    }

    setTotalAmount(totalAmount: Amount): this {
        return this.clone(this._state.set('totalAmount', totalAmount));
    }

    get pendingAmount(): Amount {
        return this._state.get('pendingAmount');
    }

    setPendingAmount(pendingAmount: Amount): this {
        return this.clone(this._state.set('pendingAmount', pendingAmount));
    }

    get paymentInstruments(): Immutable.List<PaymentInstrument> {
        return this._state.get('paymentInstruments');
    }

    setPaymentInstruments(
        paymentInstruments: Immutable.List<PaymentInstrument>
    ): this {
        return this.clone(
            this._state.set('paymentInstruments', paymentInstruments)
        );
    }

    get payments(): Immutable.List<Payment> {
        return this._state.get('payments');
    }

    setPayments(payments: Immutable.List<Payment>): this {
        return this.clone(this._state.set('payments', payments));
    }

    get fullAmountRequired(): boolean {
        return this._state.get('fullAmountRequired');
    }

    setFullAmountRequired(fullAmountRequired: boolean): this {
        return this.clone(
            this._state.set('fullAmountRequired', fullAmountRequired)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TaskSlot extends ImmutableModel {
    static fromJS(json: TaskSlotModelType): TaskSlot {
        const state: Object = Object.assign({}, json);
        state.slot = VisitSlot.fromJS(state.slot);
        return new this(Immutable.Map(state));
    }

    toJS(): TaskSlotModelType {
        return {
            id: this.id,
            slot: this.slot.toJS()
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get slot(): VisitSlot {
        return this._state.get('slot');
    }

    setSlot(slot: VisitSlot): this {
        return this.clone(this._state.set('slot', slot));
    }
}
